<script setup lang="ts">
import { computed } from 'vue';

import VDialog from '@components/dialogs/VDialog/VDialog.vue';
import VLoader from '@components/loader/VLoader.vue';
import VMessage from '@components/message/VMessage/VMessage.vue';
import VForm from '@modules/forms/components/VForm/VForm.vue';
import { useQueryClient } from '@tanstack/vue-query';
import { assert, isNumber } from '@utils/guards';

import type { IEditTrackerCountryForm, ITrackerChangeCountryOptions } from '../../types';
import type { IEditTrackerCountryEmits, IEditTrackerCountryProps } from './types';
import { useTrackerChangeCountry } from '../../composables';
import { TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_TRACKERS } from '../../constants';
import { useChangeTrackerCountryMutation } from '../../mutations';

const {
  affId,
  trackerId,
  trackersListQueryKey,
} = defineProps<IEditTrackerCountryProps>();

const emit = defineEmits<IEditTrackerCountryEmits>();

const visible = defineModel<boolean>('visible');

const queryClient = useQueryClient();

const {
  mutateAsync,
  isLoading: isSaveLoading,
  errors,
  clearError,
  clearAllErrors,
} = useChangeTrackerCountryMutation();

const trackerCountryFormOptions = computed<ITrackerChangeCountryOptions>(() => ({
  affId,
  trackerId,
  isQueryEnabled: visible.value,
}));

const {
  trackerCountryFormSchema,
  isLoadingCountries,
  isLoadingTrackerCountry,
  showSavingCountryWarning,
  isReadonlyForm,
  onChange,
} = useTrackerChangeCountry(trackerCountryFormOptions);

const isLoading = computed<boolean>(() => isLoadingCountries.value || isLoadingTrackerCountry.value);

async function onSubmit({ country_code }: IEditTrackerCountryForm): Promise<void> {
  assert(isNumber(trackerId));
  await mutateAsync({
    id: affId,
    trackerId,
    country_code,
  });

  if (trackersListQueryKey) {
    void queryClient.invalidateQueries({ queryKey: [trackersListQueryKey] });
  }
  visible.value = false;
}

function onHide(): void {
  showSavingCountryWarning.value = false;
  clearAllErrors();
  emit('hide');
}
</script>

<template>
  <VDialog
    v-model:visible="visible"
    :title="$getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_TRACKERS, 'EDIT_TRACKER_COUNTRY_TITLE')"
    @hide="onHide"
  >
    <VLoader :loading="isLoading">
      <VForm
        :schema="trackerCountryFormSchema"
        :submit-btn-label="$getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_TRACKERS, 'EDIT_TRACKER_COUNTRY_SAVE')"
        :is-loading="isSaveLoading"
        :readonly="isReadonlyForm"
        reverse-buttons
        :custom-errors="errors"
        @change="onChange"
        @submit="onSubmit"
        @interacted="clearError"
      >
        <template #info>
          <VMessage
            v-if="isReadonlyForm"
            class="input-max-width"
            severity="info"
          >
            {{ $getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_TRACKERS, 'EDIT_TRACKER_COUNTRY_CANNOT_CHANGE') }}
          </VMessage>
        </template>
      </VForm>
    </VLoader>
  </VDialog>
</template>
